/**
 * Turning a picked file into something the canvas can draw.
 *
 * Three ways in, tried in order, because no single one covers what people
 * actually have on their phones:
 *
 * 1. The browser itself — instant and free for JPEG/PNG/WEBP.
 * 2. libheif compiled to WASM, for HEIC. Only Safari decodes it natively, and
 *    it's what an iPhone hands over by default. It's a heavy module, so it is
 *    imported on demand: nobody who never picks a HEIC pays for it.
 * 3. The server, for anything else libvips can read and the browser can't.
 *
 * The server can't take over step 2: an iPhone photo is a grid of tiles, and
 * libheif refuses those over its iref reference limit — bumping the limit isn't
 * exposed through sharp, and the WASM build has no such limit.
 */

const HEIC_EXTENSION = /\.hei[cf]$/i;
const HEIC_TYPES = ['image/heic', 'image/heif', 'image/heic-sequence', 'image/heif-sequence'];

/** The extension gets a say: not every OS labels a HEIC's type on the way in. */
export function isHeic(file: File): boolean {
  return HEIC_TYPES.includes(file.type.toLowerCase()) || HEIC_EXTENSION.test(file.name);
}

/** `from-image` so a portrait shot taken sideways lands the right way up. */
const decode = (blob: Blob) => createImageBitmap(blob, { imageOrientation: 'from-image' });

interface HeifImage {
  get_width(): number;
  get_height(): number;
  display(data: ImageData, done: (result: ImageData | null) => void): void;
}

interface HeifModule {
  HeifDecoder: new () => { decode(buffer: ArrayBuffer | Uint8Array): HeifImage[] };
}

export async function decodeHeic(file: File): Promise<ImageBitmap> {
  const module = await import('libheif-js/wasm-bundle');
  const libheif = ((module as unknown as { default?: HeifModule }).default ?? module) as HeifModule;

  const [image] = new libheif.HeifDecoder().decode(await file.arrayBuffer());
  if (!image) throw new Error('HEIC_NO_IMAGE');

  const width = image.get_width();
  const height = image.get_height();
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('NO_CANVAS_CONTEXT');

  const rgba = ctx.createImageData(width, height);
  await new Promise<void>((resolve, reject) => {
    image.display(rgba, result => (result ? resolve() : reject(new Error('HEIC_DISPLAY_FAILED'))));
  });
  ctx.putImageData(rgba, 0, 0);

  // libheif has already applied the rotation, so this one goes in as-is.
  return createImageBitmap(canvas);
}

export async function decodeImageFile(
  file: File,
  convertOnServer: (file: File) => Promise<Blob>,
): Promise<ImageBitmap> {
  try {
    return await decode(file);
  } catch {
    if (isHeic(file)) return decodeHeic(file);
    return decode(await convertOnServer(file));
  }
}
